import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

type ViewerSessionResponse = {
  studyInstanceUID?: string;
  viewerUrl?: string;
};

type StatusState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string };

const getViewerSessionApiBaseUrl = () => {
  const config = (window as Window & { config?: { lunnonApiBaseUrl?: string } }).config;

  return config?.lunnonApiBaseUrl || 'https://app.lunnon.com.br';
};

const toRelativeViewerUrl = (viewerUrl: string) => {
  if (!viewerUrl) {
    return null;
  }

  try {
    const url = new URL(viewerUrl, window.location.origin);

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    if (viewerUrl.startsWith('/')) {
      return viewerUrl;
    }

    return `/${viewerUrl.replace(/^\/+/, '')}`;
  }
};

function ViewerSessionRedirect() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<StatusState>({ phase: 'loading' });

  useEffect(() => {
    if (!token) {
      setStatus({ phase: 'error', message: 'Não foi possível abrir o exame. Tente novamente pelo Lunnon.' });
      return;
    }

    const controller = new AbortController();
    const apiBaseUrl = getViewerSessionApiBaseUrl();

    const loadSession = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/viewer/session/${encodeURIComponent(token)}`,
          {
            method: 'GET',
            headers: {
              Accept: 'application/json',
            },
            credentials: 'omit',
            signal: controller.signal,
          }
        );

        if (response.ok) {
          const data = (await response.json()) as ViewerSessionResponse;
          const viewerUrl =
            toRelativeViewerUrl(data.viewerUrl || '') ||
            (data.studyInstanceUID
              ? `/viewer?StudyInstanceUIDs=${encodeURIComponent(data.studyInstanceUID)}`
              : null);

          if (!viewerUrl) {
            setStatus({ phase: 'error', message: 'Não foi possível abrir o exame. Tente novamente pelo Lunnon.' });
            return;
          }

          window.location.replace(viewerUrl);
          return;
        }

        if (response.status === 404) {
          setStatus({ phase: 'error', message: 'Sessão de visualização não encontrada.' });
          return;
        }

        if (response.status === 410) {
          setStatus({
            phase: 'error',
            message: 'Sessão de visualização expirada. Abra o exame novamente pelo Lunnon.',
          });
          return;
        }

        setStatus({ phase: 'error', message: 'Não foi possível abrir o exame. Tente novamente pelo Lunnon.' });
      } catch {
        if (controller.signal.aborted) {
          return;
        }

        setStatus({ phase: 'error', message: 'Não foi possível abrir o exame. Tente novamente pelo Lunnon.' });
      }
    };

    loadSession();

    return () => {
      controller.abort();
    };
  }, [token]);

  if (status.phase === 'error') {
    return (
      <div className="absolute flex h-full w-full items-center justify-center bg-black px-6 text-center text-white">
        <div className="max-w-md">
          <h1 className="text-[22px] font-light">{status.message}</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute flex h-full w-full items-center justify-center bg-black px-6 text-center text-white">
      <div className="max-w-md">
        <h1 className="text-[22px] font-light">Preparando visualização do exame...</h1>
      </div>
    </div>
  );
}

export default ViewerSessionRedirect;
